import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { getCachedClientData } from '../../utils/clientCache';

function PaymentSuccess({ onBackToLoans, forcedStatus, token }) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [clientColors, setClientColors] = useState(null);
    const [clientLogo, setClientLogo] = useState(null);
    
    const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');
    const status = forcedStatus || searchParams.get('status') || searchParams.get('collection_status');
    const amount = searchParams.get('amount');

    useEffect(() => {
        // Load client colors and logo from cache
        if (token) {
            const cachedClient = getCachedClientData(token);
            if (cachedClient) {
                if (cachedClient.configuration) {
                    setClientColors({
                        color0: cachedClient.configuration.header_color_0,
                        color1: cachedClient.configuration.header_color_1
                    });
                }
                if (cachedClient.logo_url) {
                    setClientLogo(cachedClient.logo_url);
                }
            }
        }
    }, [token]);

    useEffect(() => {
        if (status !== 'approved' && status !== 'success') {
            return;
        }

        if (!token) {
            return;
        }

        const dni = localStorage.getItem(token + 'dni');
        if (!dni) {
            return;
        }

        const loanUid = searchParams.get('loan_uid') || searchParams.get('external_reference');

        api.post(`/c/${token}/payment/complete`, {
            status,
            payment_id: paymentId,
            loan_uid: loanUid,
        }, {
            headers: { dni }
        }).catch((error) => {
            console.error('Error confirming payment completion:', error);
        });
    }, [status, paymentId, token, searchParams]);

    const getBackgroundStyle = () => {
        if (clientColors?.color0 && clientColors?.color1) {
            return {
                background: `linear-gradient(150deg, ${clientColors.color0} 20%, ${clientColors.color1} 99%)`
            };
        }
        // Default gradient (light gray)
        return {
            background: 'linear-gradient(150deg, #f8f9fa 0%, #e9ecef 99%)'
        };
    };

    const formatCurrency = (amount) => {
        if (!amount) return '';
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS'
        }).format(parseFloat(amount));
    };

    const handleBackToAccount = () => {
        if (onBackToLoans) {
            onBackToLoans();
        } else if (token) {
            // Navigate to home with token
            navigate('/' + token);
        } else {
            // Fallback to root
            navigate('/');
        }
    };

    if (status === 'approved' || status === 'success') {
        return (
            <div 
                className="payment-success-container min-vh-100 d-flex align-items-center justify-content-center"
                style={getBackgroundStyle()}
            >
                <div className="container">
                    {/* Client Logo */}
                    {clientLogo && (
                        <div className="text-center mb-4">
                            <img 
                                src={clientLogo.startsWith('/') ? clientLogo : `/${clientLogo}`}
                                alt="Logo"
                                style={{ 
                                    maxHeight: '60px',
                                    maxWidth: '200px',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    )}
                    
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-6">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-5 text-center">
                                    {/* Success Icon */}
                                    <div className="mb-4">
                                        <div 
                                            className="rounded-circle bg-success d-inline-flex align-items-center justify-content-center"
                                            style={{ width: '80px', height: '80px' }}
                                        >
                                            <i className="bi bi-check-lg text-white" style={{ fontSize: '3rem' }}></i>
                                        </div>
                                    </div>

                                    {/* Success Message */}
                                    <h3 className="mb-3">¡Pago exitoso!</h3>
                                    <p className="text-muted mb-4">
                                        Tu pago ha sido procesado correctamente
                                    </p>

                                    {/* Payment Details */}
                                    {amount && (
                                        <div className="bg-light rounded-3 p-4 mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted">Monto pagado</span>
                                                <span className="fw-bold fs-4 text-success">
                                                    {formatCurrency(amount)}
                                                </span>
                                            </div>
                                            {paymentId && (
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="text-muted small">ID de pago</span>
                                                    <span className="small text-muted">#{paymentId}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Information */}
                                    <div className="alert alert-info border-0" role="alert">
                                        <i className="bi bi-info-circle me-2"></i>
                                        <small>
                                            El pago puede tardar en reflejarse en tu cuenta.
                                        </small>
                                    </div>

                                    {/* Action Buttons */}
                                    <button 
                                        className="btn btn-primary w-100 py-3 rounded-pill fw-semibold"
                                        onClick={handleBackToAccount}
                                    >
                                        Volver a Mi Cuenta
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'pending') {
        return (
            <div 
                className="payment-success-container min-vh-100 d-flex align-items-center justify-content-center"
                style={getBackgroundStyle()}
            >
                <div className="container">
                    {/* Client Logo */}
                    {clientLogo && (
                        <div className="text-center mb-4">
                            <img 
                                src={clientLogo.startsWith('/') ? clientLogo : `/${clientLogo}`}
                                alt="Logo"
                                style={{ 
                                    maxHeight: '60px',
                                    maxWidth: '200px',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    )}
                    
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-6">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-5 text-center">
                                    {/* Pending Icon */}
                                    <div className="mb-4">
                                        <div 
                                            className="rounded-circle bg-warning d-inline-flex align-items-center justify-content-center"
                                            style={{ width: '80px', height: '80px' }}
                                        >
                                            <i className="bi bi-clock text-white" style={{ fontSize: '3rem' }}></i>
                                        </div>
                                    </div>

                                    {/* Pending Message */}
                                    <h3 className="mb-3">Pago en proceso</h3>
                                    <p className="text-muted mb-4">
                                        Tu pago está siendo procesado. Te notificaremos cuando se complete.
                                    </p>

                                    {/* Payment Details */}
                                    {paymentId && (
                                        <div className="bg-light rounded-3 p-4 mb-4">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted small">ID de pago</span>
                                                <span className="small text-muted">#{paymentId}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <button 
                                        className="btn btn-primary w-100 py-3 rounded-pill fw-semibold"
                                        onClick={handleBackToAccount}
                                    >
                                        Volver a Mi Cuenta
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Failed or cancelled payment
    return (
        <div 
            className="payment-success-container min-vh-100 d-flex align-items-center justify-content-center"
            style={getBackgroundStyle()}
        >
            <div className="container">
                {/* Client Logo */}
                {clientLogo && (
                    <div className="text-center mb-4">
                        <img 
                            src={clientLogo.startsWith('/') ? clientLogo : `/${clientLogo}`}
                            alt="Logo"
                            style={{ 
                                maxHeight: '60px',
                                maxWidth: '200px',
                                objectFit: 'contain'
                            }}
                        />
                    </div>
                )}
                
                <div className="row justify-content-center">
                    <div className="col-12 col-md-6">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-5 text-center">
                                {/* Error Icon */}
                                <div className="mb-4">
                                    <div 
                                        className="rounded-circle bg-danger d-inline-flex align-items-center justify-content-center"
                                        style={{ width: '80px', height: '80px' }}
                                    >
                                        <i className="bi bi-x-lg text-white" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                </div>

                                {/* Error Message */}
                                <h3 className="mb-3">Pago no completado</h3>
                                <p className="text-muted mb-4">
                                    {status === 'rejected' 
                                        ? 'Tu pago fue rechazado. Por favor, intenta con otro método de pago.'
                                        : 'El pago fue cancelado o no se pudo procesar.'
                                    }
                                </p>

                                {/* Payment Details */}
                                {paymentId && (
                                    <div className="bg-light rounded-3 p-4 mb-4">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="text-muted small">ID de referencia</span>
                                            <span className="small text-muted">#{paymentId}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="d-grid gap-2">
                                    <button 
                                        className="btn btn-primary py-3 rounded-pill fw-semibold"
                                        onClick={handleBackToAccount}
                                    >
                                        Intentar nuevamente
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary py-2 rounded-pill"
                                        onClick={handleBackToAccount}
                                    >
                                        Volver a Mi Cuenta
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PaymentSuccess;
